<template>
  <div v-if="category" class="configurator">
    <category-configurator-general :category="category" @input="onInput" />
    <category-configurator-chapters :category="category" @input="onInput" />
    <category-configurator-images :category="category" @input="onInput" />
    <category-configurator-advanced :category="category" @input="onInput" />
    <category-configurator-divisions :category="category" @input="onInput" />
    <category-configurator-judging :category="category" @input="onInput" />
    <category-configurator-delivery :category="category" @input="onInput" />

    <configuration-tray-buttons
      :disabled="saving"
      :show-delete-button="isDeletable"
      @saved="save"
      @canceled="onCanceled"
      @deleted="confirmDeletion = true"
    />

    <confirmation-modal
      modal-id="modal-target-delete-category"
      :show-modal="confirmDeletion"
      :confirmation="lang.get('category.alerts.delete')"
      :confirm-button-label="lang.get('buttons.delete')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmDeletion = false"
      @confirmed="remove"
    />

    <confirmation-modal
      modal-id="modal-target-cancel-category-configuration"
      :show-modal="confirmCancellation"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmCancellation = false"
      @confirmed="cancel"
    />
  </div>
</template>

<script>
import _ from 'underscore';
import { mapActions, mapMutations, mapState } from 'vuex';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ConfigurationTrayButtons from '../ConfigurationTrayButtons';
import CategoryConfiguratorGeneral from './CategoryConfiguratorGeneral';
import CategoryConfiguratorChapters from './CategoryConfiguratorChapters';
import CategoryConfiguratorImages from './CategoryConfiguratorImages';
import CategoryConfiguratorAdvanced from './CategoryConfiguratorAdvanced';
import CategoryConfiguratorDivisions from './CategoryConfiguratorDivisions';
import CategoryConfiguratorJudging from './CategoryConfiguratorJudging';
import CategoryConfiguratorDelivery from './CategoryConfiguratorDelivery';

export default {
  inject: ['lang'],
  components: {
    ConfirmationModal,
    ConfigurationTrayButtons,
    CategoryConfiguratorGeneral,
    CategoryConfiguratorChapters,
    CategoryConfiguratorImages,
    CategoryConfiguratorAdvanced,
    CategoryConfiguratorDivisions,
    CategoryConfiguratorJudging,
    CategoryConfiguratorDelivery
  },
  props: {
    category: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmCancellation: false,
      confirmDeletion: false
    };
  },
  computed: {
    ...mapState('entryFormConfiguration', [
      'configurationInProgress',
      'saving'
    ]),
    isDeletable() {
      return this.category.createdAt !== null;
    }
  },
  created() {
    if (this.category.createdAt) {
      this.preserveCategory({ ...this.category });
    }

    this.debouncedUpdate = _.debounce(this.updateCategory, 300);

    this.$emit('created');
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'configureCategory',
      'addCategory',
      'saveCategory',
      'deleteCategory'
    ]),
    ...mapMutations('entryForm', ['updateCategory']),
    ...mapMutations('entryFormConfiguration', [
      'preserveCategory',
      'setConfigurationInProgressFlag'
    ]),
    onInput(input) {
      if (!this.configurationInProgress) {
        this.setConfigurationInProgressFlag(true);
      }

      this.debouncedUpdate({ ...this.category, ...input });
    },
    save() {
      if (!this.category.createdAt) {
        this.addCategory({ category: this.category });
      } else {
        this.saveCategory({ category: this.category });
      }
    },
    onCanceled() {
      if (this.configurationInProgress) {
        this.confirmCancellation = true;
      } else {
        this.cancel();
      }
    },
    cancel() {
      this.confirmCancellation = false;
      this.$nextTick(() => {
        this.configureCategory({ slug: null });
      });
    },
    remove() {
      this.confirmDeletion = false;

      this.deleteCategory({ category: this.category }).then(response => {
        if (response && response.status === 200) {
          this.configureCategory({ slug: null });
        }
      });
    }
  }
};
</script>

<style scoped>
.configurator {
  margin-bottom: 150px;
}
</style>
